import { cn } from '@src/lib/utils';

import { ProcessLayoutServiceType, ServiceVM } from '@shared/types/process';

import { BlockViewTab } from './components/BlockView/BlockViewTab';
import { LayoutBloc } from './components/LayoutBloc';
import { useLayoutByRoleAndStage } from './hook/useLayoutByRoleAndStage';
import '../../index.css';
import { useLayoutProcessFields } from './hook/useLayoutProcessFields';
import { LayoutViewProvider } from './LayoutViewContext';

import { css } from '@emotion/css';
import { ShadcnSkeleton } from '@shadcn-features/suspense/ShadcnSkeleton';
import { useMemo } from 'react';

const LayoutView = ({
    layoutProcess,
    service,
    childrenBtnForFlowTab,
    classNameWrapper,
    tabProps,
    fileUploadController = 'flow/Services',
    layoutVariant,
}: {
    classNameWrapper?: string;
    fileUploadController?: string;
    layoutProcess: ProcessLayoutServiceType;
    service?: ServiceVM;
    childrenBtnForFlowTab?: React.ReactNode;
    tabProps?: React.ComponentPropsWithoutRef<typeof BlockViewTab>['tabProps'];
    layoutVariant?: 'recipient' | 'provider';
}) => {
    const { layoutBlocks } = useLayoutByRoleAndStage({
        layout: layoutProcess,
    });

    const mainLayoutVariant = useMemo(() => {
        if (layoutVariant) {
            return layoutVariant;
        } else {
            return layoutProcess.isForServiceRecipient === false
                ? 'provider'
                : 'recipient';
        }
    }, [layoutProcess.isForServiceRecipient, layoutVariant]);

    const normalizeBlocks = useMemo(() => {
        return sortByY(layoutBlocks);
    }, [layoutBlocks]);

    const { isLoading, processFieldsById } = useLayoutProcessFields({
        serviceId: service?.id,
        layoutId: layoutProcess.id,
    });

    return (
        <LayoutViewProvider
            value={{
                fileUploadController,
                mainLayoutVariant,
                processFieldsById,
            }}
        >
            <div
                data-scope-office
                className='w-full [--form-item-margin-bottom:0px]'
            >
                <div
                    className={cn(css`
                        margin: 0 auto;
                        border-radius: 10px;
                    `)}
                >
                    <ShadcnSkeleton
                        fallbackClassName='min-h-40'
                        override={{ loading: isLoading }}
                    >
                        <div
                            className={cn(
                                'serviceForm',
                                'grid grid-cols-3',
                                !classNameWrapper && 'gap-x-3',
                                classNameWrapper,
                            )}
                        >
                            {normalizeBlocks.map((block) => (
                                <div
                                    key={block.id}
                                    className='empty:hidden'
                                    style={{
                                        height: 'fit-content',
                                        gridColumn:
                                            'span ' + block.coordinates.w,
                                        gridColumnStart:
                                            block.coordinates.x + 1,
                                        gridColumnEnd:
                                            block.coordinates.x +
                                            block.coordinates.w +
                                            1,
                                    }}
                                >
                                    <LayoutBloc
                                        isParent
                                        block={block}
                                        classNameWrapper={classNameWrapper}
                                        service={service}
                                        tabProps={tabProps}
                                        childrenBtnForFlowTab={
                                            childrenBtnForFlowTab
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </ShadcnSkeleton>
                </div>
            </div>
        </LayoutViewProvider>
    );
};

export default LayoutView;

function sortByY<
    T extends { coordinates: { x: number; y: number }; children?: T[] },
>(nodes: T[]): T[] {
    return [...nodes]
        .sort(
            (a, b) =>
                a.coordinates.y - b.coordinates.y ||
                a.coordinates.x - b.coordinates.x,
        )
        .map((node) => ({
            ...node,
            children: node.children ? sortByY(node.children) : [],
        }));
}
